// Pure helpers for editing a FormDefinition. No React, no I/O: every function
// returns new objects so the builder can treat the definition as immutable state.
// Validation wording mirrors backend/internal/forms/validate.go so client and
// server problems de-duplicate cleanly (the server list stays authoritative).
import { validate as validateCondition, type Condition } from '@/lib/expr'
import type { ConditionField } from '../../components/ConditionEditor'
import type { AIReview, FieldOption, FieldType, FormDefinition, FormField, FormPage, FormRule, Severity } from '../../types'

export const KEY_RE = /^[a-z][a-z0-9_]{0,63}$/
export const SEVERITIES: Severity[] = ['high', 'medium', 'low', 'info']

export const SELECT_TYPES: FieldType[] = ['single_select', 'multi_select']
export const TEXT_TYPES: FieldType[] = ['text', 'textarea']
export const isSelectType = (t: FieldType) => SELECT_TYPES.includes(t)

// ---------------------------------------------------------------- strings

/** lower_snake_case: letters/digits only, collapsed separators, no edge underscores. */
export function slugify(s: string): string {
    return s
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
}

/** A valid field key derived from a label ('' label gives the fallback). */
export function keyFromLabel(label: string, fallback = 'field'): string {
    let k = slugify(label)
    if (!k) k = fallback
    if (!/^[a-z]/.test(k)) k = `q_${k}`
    return k.slice(0, 64).replace(/_+$/, '') || fallback
}

/** `base`, or `base_2`, `base_3`… the first not in `taken` (keeps within 64 chars). */
export function uniqueKey(base: string, taken: Iterable<string>): string {
    const set = taken instanceof Set ? (taken as Set<string>) : new Set(taken)
    if (!set.has(base)) return base
    for (let n = 2; ; n++) {
        const suffix = `_${n}`
        const cand = base.slice(0, 64 - suffix.length) + suffix
        if (!set.has(cand)) return cand
    }
}

export function optionValueFromLabel(label: string): string {
    return slugify(label) || 'option'
}

export function moveItem<T>(arr: readonly T[], index: number, delta: number): T[] {
    const to = index + delta
    if (index < 0 || index >= arr.length || to < 0 || to >= arr.length) return arr.slice()
    const out = arr.slice()
    const [it] = out.splice(index, 1)
    out.splice(to, 0, it)
    return out
}

// ----------------------------------------------------------------- lookups

export function normalizeDefinition(def: FormDefinition | null | undefined): FormDefinition {
    return {
        pages: (def?.pages ?? []).map((p) => ({ ...p, fields: (p.fields ?? []).map((f) => ({ ...f })) })),
        rules: def?.rules ?? [],
        ...(def?.ai ? { ai: { ...def.ai } } : {}),
    }
}

/**
 * Update the AI review settings. The default (on, no instructions) is stored as
 * "no ai block" so untouched forms stay unchanged.
 */
export function setAIReview(def: FormDefinition, patch: Partial<AIReview>): FormDefinition {
    const next: AIReview = { ...def.ai, ...patch }
    if (!next.disabled) delete next.disabled
    if (!next.instructions || !next.instructions.trim()) delete next.instructions
    const { ai: _old, ...rest } = def
    void _old
    return Object.keys(next).length === 0 ? rest : { ...rest, ai: next }
}

export function allFieldKeys(def: FormDefinition): Set<string> {
    const s = new Set<string>()
    for (const p of def.pages) for (const f of p.fields) s.add(f.key)
    return s
}

export function toConditionField(f: FormField): ConditionField {
    return { key: f.key, label: f.label || f.key, type: f.type, options: f.options }
}

/** Distinct, answerable fields (no html, one entry per key) as condition operands. */
export function toConditionFields(fields: FormField[]): ConditionField[] {
    const seen = new Set<string>()
    const out: ConditionField[] = []
    for (const f of fields) {
        if (f.type === 'html' || !f.key || seen.has(f.key)) continue
        seen.add(f.key)
        out.push(toConditionField(f))
    }
    return out
}

export function allFields(def: FormDefinition): ConditionField[] {
    return toConditionFields(def.pages.flatMap((p) => p.fields))
}

/** Fields a field's `visibleWhen` may point at: earlier pages + earlier fields on its page. */
export function precedingFields(def: FormDefinition, pageId: string, index: number): ConditionField[] {
    const out: FormField[] = []
    for (const p of def.pages) {
        if (p.id === pageId) {
            out.push(...p.fields.slice(0, index))
            break
        }
        out.push(...p.fields)
    }
    const self = def.pages.find((p) => p.id === pageId)?.fields[index]
    return toConditionFields(out).filter((f) => f.key !== self?.key)
}

/** Fields a page's `activeWhen` may point at: every field on the OTHER pages. */
export function otherPagesFields(def: FormDefinition, pageId: string): ConditionField[] {
    return toConditionFields(def.pages.filter((p) => p.id !== pageId).flatMap((p) => p.fields))
}

// ------------------------------------------------------------ conditions

/** Rewrite references to `from` as `to` throughout a condition tree. */
export function renameInCondition(c: Condition | undefined, from: string, to: string): Condition | undefined {
    if (!c) return c
    if ('field' in c) return c.field === from ? { ...c, field: to } : c
    if ('not' in c) return { not: renameInCondition(c.not, from, to) as Condition }
    if ('all' in c) return { all: c.all.map((x) => renameInCondition(x, from, to) as Condition) }
    return { any: c.any.map((x) => renameInCondition(x, from, to) as Condition) }
}

export function conditionFields(c: Condition | undefined): string[] {
    if (!c) return []
    if ('field' in c) return [c.field]
    if ('not' in c) return conditionFields(c.not)
    return ('all' in c ? c.all : c.any).flatMap(conditionFields)
}

/** Does the condition reference nothing at all (an empty group)? */
export function isEmptyCondition(c: Condition | undefined): boolean {
    return !c || conditionFields(c).length === 0
}

// ------------------------------------------------------------------ pages

export function newPageId(def: FormDefinition): string {
    const ids = new Set(def.pages.map((p) => p.id))
    return uniqueKey(`page_${def.pages.length + 1}`, ids)
}

export function addPage(def: FormDefinition, title = ''): { def: FormDefinition; pageId: string } {
    const pageId = newPageId(def)
    const page: FormPage = { id: pageId, title: title || `Page ${def.pages.length + 1}`, fields: [] }
    return { def: { ...def, pages: [...def.pages, page] }, pageId }
}

export function updatePage(def: FormDefinition, pageId: string, patch: Partial<FormPage>): FormDefinition {
    return { ...def, pages: def.pages.map((p) => (p.id === pageId ? { ...p, ...patch } : p)) }
}

export function removePage(def: FormDefinition, pageId: string): FormDefinition {
    return { ...def, pages: def.pages.filter((p) => p.id !== pageId) }
}

export function movePage(def: FormDefinition, pageId: string, delta: number): FormDefinition {
    const i = def.pages.findIndex((p) => p.id === pageId)
    return { ...def, pages: moveItem(def.pages, i, delta) }
}

// ----------------------------------------------------------------- fields

export const TYPE_LABEL: Record<FieldType, string> = {
    text: 'Short text',
    textarea: 'Long text',
    number: 'Number',
    yes_no: 'Yes/No',
    single_select: 'Single select',
    multi_select: 'Multi select',
    date: 'Date',
    datetime: 'Date & time',
    file: 'File/link',
    user_reference: 'Person',
    country: 'Country',
    html: 'Info text (html)',
}

export const FIELD_TYPES: FieldType[] = [
    'text', 'textarea', 'number', 'yes_no', 'single_select', 'multi_select',
    'date', 'datetime', 'file', 'user_reference', 'country', 'html',
]

export function newField(type: FieldType, takenKeys: Iterable<string>): FormField {
    const base = type === 'html' ? 'info' : 'new_field'
    const f: FormField = { key: uniqueKey(base, takenKeys), type, label: '' }
    if (isSelectType(type)) f.options = [{ value: 'option_1', label: 'Option 1' }]
    return f
}

export function addField(
    def: FormDefinition, pageId: string, type: FieldType, at?: number,
): { def: FormDefinition; index: number; key: string } {
    const field = newField(type, allFieldKeys(def))
    let index = -1
    const pages = def.pages.map((p) => {
        if (p.id !== pageId) return p
        const fields = p.fields.slice()
        index = at === undefined || at > fields.length ? fields.length : Math.max(0, at)
        fields.splice(index, 0, field)
        return { ...p, fields }
    })
    return { def: { ...def, pages }, index, key: field.key }
}

function mapFields(def: FormDefinition, pageId: string, fn: (fields: FormField[]) => FormField[]): FormDefinition {
    return { ...def, pages: def.pages.map((p) => (p.id === pageId ? { ...p, fields: fn(p.fields) } : p)) }
}

/**
 * Patch one field. When the key changes, references to it in conditions and
 * rules are rewritten so a rename never silently breaks logic.
 */
export function updateField(def: FormDefinition, pageId: string, index: number, patch: Partial<FormField>): FormDefinition {
    const old = def.pages.find((p) => p.id === pageId)?.fields[index]
    if (!old) return def
    let next = mapFields(def, pageId, (fs) => fs.map((f, i) => (i === index ? { ...f, ...patch } : f)))
    if (patch.key !== undefined && patch.key !== old.key && old.key) {
        const from = old.key, to = patch.key
        // Only rewrite when no other field still carries the old key (shared keys across pages).
        const stillUsed = next.pages.some((p) => p.fields.some((f) => f.key === from))
        if (!stillUsed) {
            next = {
                pages: next.pages.map((p) => ({
                    ...p,
                    activeWhen: renameInCondition(p.activeWhen, from, to),
                    fields: p.fields.map((f) => (f.visibleWhen ? { ...f, visibleWhen: renameInCondition(f.visibleWhen, from, to) } : f)),
                })),
                rules: next.rules.map((r) => ({
                    ...r,
                    when: renameInCondition(r.when, from, to) as Condition,
                    field: r.field === from ? to : r.field,
                })),
            }
        }
    }
    return next
}

export function removeField(def: FormDefinition, pageId: string, index: number): FormDefinition {
    return mapFields(def, pageId, (fs) => fs.filter((_, i) => i !== index))
}

export function moveField(def: FormDefinition, pageId: string, index: number, delta: number): FormDefinition {
    return mapFields(def, pageId, (fs) => moveItem(fs, index, delta))
}

export function duplicateField(def: FormDefinition, pageId: string, index: number): { def: FormDefinition; index: number } {
    const src = def.pages.find((p) => p.id === pageId)?.fields[index]
    if (!src) return { def, index }
    const copy: FormField = {
        ...JSON.parse(JSON.stringify(src)),
        key: uniqueKey(`${src.key}_copy`.slice(0, 64), allFieldKeys(def)),
        label: src.label ? `${src.label} (copy)` : src.label,
    }
    delete copy.stableId
    return { def: mapFields(def, pageId, (fs) => [...fs.slice(0, index + 1), copy, ...fs.slice(index + 1)]), index: index + 1 }
}

/** Change a field's type, keeping what still makes sense. `lost` names what was dropped. */
export function changeFieldType(field: FormField, type: FieldType): { field: FormField; lost: string[] } {
    if (field.type === type) return { field, lost: [] }
    const next: FormField = { ...field, type }
    const lost: string[] = []
    if (isSelectType(type)) {
        if (!next.options?.length) next.options = [{ value: 'option_1', label: 'Option 1' }]
    } else if (next.options?.length) {
        lost.push(`${next.options.length} option${next.options.length === 1 ? '' : 's'}`)
        delete next.options
    }
    if (!TEXT_TYPES.includes(type) && next.maxLength) {
        lost.push('max length')
        delete next.maxLength
    }
    if (type === 'html') {
        if (next.required) lost.push('required')
        if (next.visibleWhen) { /* html may still be conditional */ }
        delete next.required
        delete next.help
    }
    return { field: next, lost }
}

// ---------------------------------------------------------------- options

export function addOption(options: FieldOption[] | undefined, label = ''): FieldOption[] {
    const list = options ?? []
    const n = list.length + 1
    const l = label || `Option ${n}`
    const value = uniqueKey(optionValueFromLabel(l), list.map((o) => o.value))
    return [...list, { value, label: l }]
}

export function updateOption(options: FieldOption[], index: number, patch: Partial<FieldOption>): FieldOption[] {
    return options.map((o, i) => (i === index ? { ...o, ...patch } : o))
}

export function removeOption(options: FieldOption[], index: number): FieldOption[] {
    return options.filter((_, i) => i !== index)
}

export function moveOption(options: FieldOption[], index: number, delta: number): FieldOption[] {
    return moveItem(options, index, delta)
}

/** Append one option per non-empty line; values are slugged and made unique. */
export function bulkAddOptions(options: FieldOption[] | undefined, text: string): FieldOption[] {
    let out = options ?? []
    for (const raw of text.split(/\r?\n/)) {
        const label = raw.trim()
        if (label) out = addOption(out, label)
    }
    return out
}

// ------------------------------------------------------------------ rules

export function nextRuleId(rules: FormRule[]): string {
    const ids = new Set(rules.map((r) => r.id))
    let n = rules.length + 1
    while (ids.has(`rule_${n}`)) n++
    return `rule_${n}`
}

export function addRule(def: FormDefinition, rule: FormRule): FormDefinition {
    return { ...def, rules: [...def.rules, rule] }
}

export function updateRule(def: FormDefinition, index: number, rule: FormRule): FormDefinition {
    return { ...def, rules: def.rules.map((r, i) => (i === index ? rule : r)) }
}

export function removeRule(def: FormDefinition, index: number): FormDefinition {
    return { ...def, rules: def.rules.filter((_, i) => i !== index) }
}

export function moveRule(def: FormDefinition, index: number, delta: number): FormDefinition {
    return { ...def, rules: moveItem(def.rules, index, delta) }
}

// ------------------------------------------------------------- validation

export interface Problem {
    message: string
    pageId?: string
    key?: string
}

/** Client-side mirror of Definition.Validate() for instant hints. */
export function validateDefinition(def: FormDefinition): Problem[] {
    const out: Problem[] = []
    const add = (message: string, pageId?: string, key?: string) => out.push({ message, pageId, key })
    const q = (s: string) => JSON.stringify(s)

    if (def.pages.length === 0) add('form needs at least one page')
    const pageIds = new Set<string>()
    const keys = new Set<string>()
    const keyType = new Map<string, string>()
    for (const p of def.pages) {
        const onPage = new Set<string>()
        if (!p.id) add(`page ${q(p.title)} has no id`, p.id)
        else if (pageIds.has(p.id)) add(`duplicate page id ${q(p.id)}`, p.id)
        pageIds.add(p.id)
        for (const f of p.fields) {
            if (!KEY_RE.test(f.key)) add(`field key ${q(f.key)} must match ${KEY_RE.source}`, p.id, f.key)
            if (onPage.has(f.key)) add(`duplicate field key ${q(f.key)} on page ${q(p.id)}`, p.id, f.key)
            onPage.add(f.key)
            const t = keyType.get(f.key)
            if (t !== undefined && t !== f.type) add(`field key ${q(f.key)} is used with conflicting types ${q(t)} and ${q(f.type)}`, p.id, f.key)
            keyType.set(f.key, f.type)
            keys.add(f.key)
            if (f.type !== 'html' && f.label.trim() === '') add(`field ${q(f.key)} has no label`, p.id, f.key)
            if (isSelectType(f.type) && (f.options?.length ?? 0) === 0) add(`field ${q(f.key)} is a select with no options`, p.id, f.key)
            const vals = new Set<string>()
            for (const o of f.options ?? []) {
                if (o.value === '') add(`field ${q(f.key)} has an option with an empty value`, p.id, f.key)
                if (vals.has(o.value)) add(`field ${q(f.key)} has duplicate option value ${q(o.value)}`, p.id, f.key)
                vals.add(o.value)
            }
        }
    }
    const checkCond = (where: string, c: Condition, pageId?: string, key?: string) => {
        const err = validateCondition(c)
        if (err) return add(`${where}: ${err}`, pageId, key)
        for (const k of conditionFields(c)) if (!keys.has(k)) add(`${where} references unknown field ${q(k)}`, pageId, key)
    }
    for (const p of def.pages) {
        if (p.activeWhen) checkCond(`page ${p.id} activeWhen`, p.activeWhen, p.id)
        for (const f of p.fields) if (f.visibleWhen) checkCond(`field ${f.key} visibleWhen`, f.visibleWhen, p.id, f.key)
    }
    const ruleIds = new Set<string>()
    for (const r of def.rules) {
        if (!r.id) add(`rule ${q(r.message)} has no id`)
        else if (ruleIds.has(r.id)) add(`duplicate rule id ${q(r.id)}`)
        ruleIds.add(r.id)
        if (!SEVERITIES.includes(r.severity)) add(`rule ${r.id} has invalid severity ${q(String(r.severity))}`)
        if (r.message.trim() === '') add(`rule ${r.id} has no message`)
        checkCond(`rule ${r.id}`, r.when)
        if (r.field && !keys.has(r.field)) add(`rule ${r.id} names unknown field ${q(r.field)}`)
    }
    return out
}

/** Best-effort mapping of a server problem string back to a page/field. */
export function locateProblem(def: FormDefinition, message: string): { pageId: string; key?: string } | null {
    const fieldM = /(?:field(?: key)?) "([^"]+)"/.exec(message) ?? /\bfield ([a-z][a-z0-9_]*)/.exec(message)
    if (fieldM) {
        const p = def.pages.find((pg) => pg.fields.some((f) => f.key === fieldM[1]))
        if (p) return { pageId: p.id, key: fieldM[1] }
    }
    const pageM = /page "?([^"\s]+)"?/.exec(message)
    if (pageM && def.pages.some((p) => p.id === pageM[1])) return { pageId: pageM[1] }
    return null
}

export function describeShort(def: FormDefinition): string {
    const f = def.pages.reduce((n, p) => n + p.fields.length, 0)
    return `${def.pages.length} page${def.pages.length === 1 ? '' : 's'}, ${f} field${f === 1 ? '' : 's'}, ${def.rules.length} rule${def.rules.length === 1 ? '' : 's'}`
}

/** Replace a whole field in place (key must be unchanged; use updateField to rename). */
export function replaceField(def: FormDefinition, pageId: string, index: number, field: FormField): FormDefinition {
    return mapFields(def, pageId, (fs) => fs.map((f, i) => (i === index ? field : f)))
}
